/**
 * RequestShowingModal — unified Showami showing-request UI with credit + payment gate.
 *
 * Views:
 *   - 'form': submit a new request (pre-filled from `listing` prop if provided)
 *   - 'list': see past/current showings with status, agent contact, cancel
 *
 * Credit flow:
 *   Borrowers get 3 complimentary showings (see utils/showingCredits.js).
 *   This modal shows the remaining count in the header. When the borrower
 *   hits submit:
 *     - If credits > 0: backend deducts one + submits to Showami inline.
 *     - If credits === 0: backend creates a pending row and returns
 *       { payment_required: true, showing_id }. We then mount <PaymentModal>
 *       for the $60 showing fee; on success we POST back to
 *       /api/showami/request with { paid_showing_id } to trigger the
 *       Showami submission.
 *
 * Props:
 *   listing      — optional listing object with `.address.{street,city,state,zip}`
 *                  and/or top-level `street/city/state/zip`. Used to pre-fill.
 *   initialView  — 'form' | 'list' (default 'form'). Clicking a listing's CTA
 *                  should pass 'form'; a header "My Showings" button → 'list'.
 *   onClose      — invoked when the user closes the modal
 *   token        — borrower auth token
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import PaymentModal from './PaymentModal';
import AddressAutocomplete from './AddressAutocomplete';
import { formatPhone } from '../utils/phone';
import {
  MIN_NOTICE_HOURS,
  isTimeSlotDisabled,
  isWithin4Hours,
  earliestValidDateStr,
  TIME_SLOTS,
  maxValidDateStr,
} from '../utils/showingDateUtils';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD',
  'MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC',
  'SD','TN','TX','UT','VT','VA','WA','WV','WI','WY',
];

const STATUS_STYLE = {
  // "Pending Payment" was retired in commit ab7dbc1 (rows are only ever
  // inserted after Showami succeeds). Any legacy row in that state falls
  // through to the Pending styling below.
  'Pending':            { bg: '#9ca3af', color: '#ffffff', label: 'Pending'   },
  'Accepted':           { bg: '#3a6ad9', color: '#ffffff', label: 'Accepted'  },
  'Confirmed':          { bg: '#1e40af', color: '#ffffff', label: 'Confirmed' },
  'Completed':          { bg: '#acf44e', color: '#1a1a1a', label: 'Completed' },
  'Canceled':           { bg: '#ff082f', color: '#ffffff', label: 'Canceled'  },
  pending_confirmation: { bg: '#9ca3af', color: '#ffffff', label: 'Pending'   },
  pending:              { bg: '#9ca3af', color: '#ffffff', label: 'Pending'   },
  accepted:             { bg: '#3a6ad9', color: '#ffffff', label: 'Accepted'  },
  confirmed:            { bg: '#1e40af', color: '#ffffff', label: 'Confirmed' },
  completed:            { bg: '#acf44e', color: '#1a1a1a', label: 'Completed' },
  canceled:             { bg: '#ff082f', color: '#ffffff', label: 'Canceled'  },
  pending_payment:      { bg: '#9ca3af', color: '#ffffff', label: 'Pending'   },
};

function StatusBadge({ status }) {
  const s = STATUS_STYLE[status] || STATUS_STYLE.Pending;
  return (
    <span style={{ display: 'inline-block', padding: '3px 10px', borderRadius: 9999, fontSize: 11, fontWeight: 700, background: s.bg, color: s.color, letterSpacing: 0.3, textTransform: 'uppercase' }}>
      {s.label}
    </span>
  );
}

// Display a 10-digit phone as "(555) 123-4567" — preserves whatever the user
// has typed so far without discarding partial digits.
// ⚠️ MOVED TO utils/phone.js — see the note in DoorlyProRegister.

// ⚠️ THE SLOT GRID MOVED TO showingDateUtils.js. It lived here, and the Homes
// "Submit a property" form had no equivalent — it offered PERIODS into the same
// column. One grid, imported by both, is what keeps them from disagreeing again.

// Date min/max — delegated to the shared util so the 24-hour minimum
// notice + 7-day window rules live in one place (see
// frontend/src/utils/showingDateUtils.js).
const maxDate = () => maxValidDateStr();
const earliestValidDate = () => earliestValidDateStr();

function prefillFromListing(listing) {
  if (!listing) return null;
  const addr = listing.address || listing;
  return {
    property_address: addr.street || addr.line1 || addr.address_street || '',
    property_city:    addr.city   || '',
    property_state:   addr.state  || 'TX',
    property_zip:     addr.zip    || addr.zipcode || addr.postal_code || '',
  };
}

export default function RequestShowingModal({ listing, initialView = 'form', onClose, token }) {
  const pre = prefillFromListing(listing);
  const [view, setView] = useState(initialView);

  const [form, setForm] = useState({
    property_address: pre?.property_address || '',
    property_city:    pre?.property_city    || '',
    property_state:   pre?.property_state   || 'TX',
    property_zip:     pre?.property_zip     || '',
    requested_date:   earliestValidDate(),
    requested_time:   '',  // user must pick from the dropdown
    contact_phone:    '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitErr, setSubmitErr] = useState('');
  const [done, setDone] = useState(false);

  // Credit balance
  const [credits, setCredits] = useState(null);

  // Payment gate. We hold the validated form payload locally while the
  // borrower is paying — the backend doesn't create a showing_requests
  // row until both Stripe and Showami have succeeded, so there's no
  // showing_id to track at this stage.
  const [pendingForm, setPendingForm] = useState(null);
  const [showPayment, setShowPayment] = useState(false);
  const [finalizing, setFinalizing] = useState(false);

  const [requests, setRequests] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [listErr, setListErr] = useState('');
  const [refreshingId, setRefreshingId] = useState(null);
  // Per-showing message threads. Lazy-loaded the first time the
  // borrower clicks "View Messages" on a row, then cached. Hitting
  // GET /api/showami/requests/:id/messages also marks the thread
  // is_read_borrower=TRUE on the server side, so the unread indicator
  // clears on view.
  const [threadById, setThreadById] = useState({});
  const [threadOpenId, setThreadOpenId] = useState(null);

  // Cancellation-policy modal: blocks the final submit until the
  // borrower acknowledges the 4-hour credit-forfeiture rule. Holds
  // a single bit — the actual form payload is already in component
  // state when this fires.
  const [showCancellationPolicy, setShowCancellationPolicy] = useState(false);

  // Reschedule + cancel UX state for the My Showings list.
  //   confirm: { kind: 'cancel'|'reschedule', showing, within4h }
  //     drives the warning modal that fires before the destructive
  //     action — the "within4h" flag toggles the modal copy + button
  //     labels between "credit refunded" and "credit forfeited".
  //   rescheduleForId: scalar showing id of whichever row is showing
  //     its inline reschedule form. Null when no row is open.
  //   rescheduleForm: per-form date/time draft for the active row.
  const [confirmAction, setConfirmAction] = useState(null);
  const [actionBusy, setActionBusy] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [rescheduleForId, setRescheduleForId] = useState(null);
  const [rescheduleForm, setRescheduleForm] = useState({ requested_date: earliestValidDate(), requested_time: '' });

  const headers = { Authorization: `Bearer ${token}` };

  const fetchCredits = useCallback(async () => {
    if (!token) return;
    try {
      const { data } = await axios.get(`${API_URL}/api/homes/showing-credits`, { headers });
      setCredits(data);
      // Pre-fill the phone if the borrower hasn't typed one yet
      if (data?.phone) {
        setForm(p => p.contact_phone ? p : { ...p, contact_phone: formatPhone(data.phone) });
      }
    } catch (_) { /* non-fatal */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { fetchCredits(); }, [fetchCredits]);

  const fetchList = useCallback(async () => {
    if (!token) return;
    setLoadingList(true); setListErr('');
    try {
      const { data } = await axios.get(`${API_URL}/api/showami/requests`, { headers });
      setRequests(data.requests || []);
    } catch (err) {
      setListErr(err.response?.data?.error || err.message);
    } finally {
      setLoadingList(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    if (view === 'list') fetchList();
  }, [view, fetchList]);

  // Lock body scroll while modal is open
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape' && !showPayment) onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => { document.body.style.overflow = prev; window.removeEventListener('keydown', onKey); };
  }, [onClose, showPayment]);

  const setField = (k) => (e) => {
    setForm(p => ({ ...p, [k]: e.target.value }));
    if (submitErr) setSubmitErr('');
  };

  // Submit flow is now two-step:
  //   handleSubmit  → form onSubmit; runs all validation, and on
  //                   success raises the cancellation-policy modal
  //                   instead of firing the API directly.
  //   handleConfirmSubmit → invoked by the modal's "Confirm Showing"
  //                   button; runs the actual API call. Keeping the
  //                   validation in handleSubmit means the borrower
  //                   sees inline form errors before they reach the
  //                   modal step.
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.requested_time) {
      setSubmitErr('Please select a time.');
      return;
    }
    const m = /^(\d{2}):(\d{2})$/.exec(form.requested_time);
    if (!m) {
      setSubmitErr('Showings must be scheduled at least 1 hour in advance, between 8:00 AM and 8:45 PM, on the quarter hour');
      return;
    }
    const hour = Number(m[1]);
    const minute = Number(m[2]);
    const [yy, mm, dd] = form.requested_date.split('-').map(Number);
    const when = new Date(yy, (mm || 1) - 1, dd || 1, hour, minute);
    if (isNaN(when.getTime())) {
      setSubmitErr('Invalid date/time.');
      return;
    }
    const diffMs = when.getTime() - Date.now();
    if (diffMs < MIN_NOTICE_HOURS * 60 * 60 * 1000) {
      setSubmitErr(`Showings require at least ${MIN_NOTICE_HOURS} hours advance notice.`);
      return;
    }
    if (diffMs > 7 * 24 * 60 * 60 * 1000) {
      setSubmitErr('Showings must be within 7 days from now.');
      return;
    }
    const totalMinutes = hour * 60 + minute;
    if (totalMinutes < 8 * 60 || totalMinutes > 20 * 60 + 45 || ![0, 15, 30, 45].includes(minute)) {
      setSubmitErr('Showings must be scheduled at least 1 hour in advance, between 8:00 AM and 8:45 PM, on the quarter hour');
      return;
    }
    const phoneDigits = String(form.contact_phone || '').replace(/\D/g, '');
    if (phoneDigits.length !== 10) {
      setSubmitErr('Please enter a 10-digit phone number for the showing agent to reach you.');
      return;
    }
    setSubmitErr('');
    // Stash the validated payload + raise the cancellation-policy
    // modal. The actual API call happens in handleConfirmSubmit when
    // the borrower clicks "Confirm Showing".
    const formPayload = { ...form, contact_phone: phoneDigits, requested_at_iso: when.toISOString() };
    setPendingForm(formPayload);
    setShowCancellationPolicy(true);
  };

  const handleConfirmSubmit = async () => {
    if (!pendingForm) return;
    setShowCancellationPolicy(false);
    setSubmitting(true);
    setSubmitErr('');
    const formPayload = pendingForm;
    try {
      const { data } = await axios.post(`${API_URL}/api/showami/request`, formPayload, { headers });
      if (data.payment_required) {
        // Hold the validated payload — handlePaymentSuccess will re-send it
        // alongside the verified payment_intent_id once Stripe confirms.
        setPendingForm(formPayload);
        setShowPayment(true);
      } else {
        setDone(true);
        fetchCredits();
      }
    } catch (err) {
      setSubmitErr(err.response?.data?.error || err.message || 'Submission failed.');
    } finally {
      setSubmitting(false);
    }
  };

  // Called from PaymentModal.onSuccess after Stripe confirms the $60 charge.
  // The backend re-verifies the payment_intent_id, then calls Showami, and
  // only then creates the showing_requests row — so we don't have an id
  // until this call returns.
  const handlePaymentSuccess = async (paymentData) => {
    setShowPayment(false);
    if (!pendingForm) return;
    const paymentIntentId = paymentData?.payment_intent_id;
    if (!paymentIntentId) {
      setSubmitErr('Payment confirmation missing — please contact support.');
      return;
    }
    setFinalizing(true); setSubmitErr('');
    try {
      await axios.post(
        `${API_URL}/api/showami/request`,
        { ...pendingForm, payment_intent_id: paymentIntentId },
        { headers }
      );
      setDone(true);
      fetchCredits();
    } catch (err) {
      setSubmitErr(err.response?.data?.error || 'Payment went through but submission to Showami failed. Our team has been notified.');
    } finally {
      setFinalizing(false);
      setPendingForm(null);
    }
  };

  const handlePaymentCancel = () => {
    setShowPayment(false);
    setPendingForm(null);
  };

  const refreshStatus = async (id) => {
    setRefreshingId(id);
    try { await axios.get(`${API_URL}/api/showami/requests/${id}/status`, { headers }); fetchList(); }
    catch {} finally { setRefreshingId(null); }
  };

  // 2026-05-11 — new cancel flow with the 4hr-credit-forfeiture
  // warning. Wraps the existing DELETE endpoint but routes through the
  // confirmAction modal first so the borrower sees the policy callout.
  const handleCancelClicked = (showing) => {
    setActionError(null);
    setConfirmAction({
      kind: 'cancel',
      showing,
      within4h: isWithin4Hours(showing),
    });
  };

  const handleRescheduleClicked = (showing) => {
    setActionError(null);
    const within4h = isWithin4Hours(showing);
    if (within4h) {
      // Show the credit-forfeiture warning first — the inline reschedule
      // form only opens after the borrower confirms.
      setConfirmAction({ kind: 'reschedule', showing, within4h: true });
    } else {
      // Outside the 4h window — open the reschedule form directly.
      setRescheduleForm({ requested_date: earliestValidDate(), requested_time: '' });
      setRescheduleForId(showing.id);
    }
  };

  const confirmCancel = async () => {
    if (!confirmAction?.showing) return;
    const sid = confirmAction.showing.id;
    setActionBusy(`cancel-${sid}`);
    setActionError(null);
    try {
      const r = await axios.delete(`${API_URL}/api/showami/requests/${sid}`, { headers });
      setConfirmAction(null);
      fetchCredits();
      fetchList();
      // Surface the result message inline at the top of the list so the
      // borrower sees whether their credit came back.
      if (r.data?.message) setListErr(r.data.message);
    } catch (err) {
      setActionError(err.response?.data?.error || err.message || 'Cancellation failed.');
    } finally {
      setActionBusy(null);
    }
  };

  const confirmRescheduleProceed = () => {
    if (!confirmAction?.showing) return;
    setRescheduleForm({ requested_date: earliestValidDate(), requested_time: '' });
    setRescheduleForId(confirmAction.showing.id);
    setConfirmAction(null);
  };

  const submitReschedule = async (showingId) => {
    if (!rescheduleForm.requested_date || !rescheduleForm.requested_time) {
      setActionError('Pick a date and time before confirming.');
      return;
    }
    const [yy, mm, dd] = rescheduleForm.requested_date.split('-').map(Number);
    const [h24, m24] = rescheduleForm.requested_time.split(':').map(Number);
    const when = new Date(yy, (mm || 1) - 1, dd || 1, h24, m24);
    if (when.getTime() - Date.now() < MIN_NOTICE_HOURS * 60 * 60 * 1000) {
      setActionError(`New time must be at least ${MIN_NOTICE_HOURS} hours from now.`);
      return;
    }
    setActionBusy(`reschedule-${showingId}`);
    setActionError(null);
    try {
      const r = await axios.post(`${API_URL}/api/showami/requests/${showingId}/reschedule`,
        { new_date: rescheduleForm.requested_date, new_time: rescheduleForm.requested_time, requested_at_iso: when.toISOString() },
        { headers });
      setRescheduleForId(null);
      fetchList();
      if (r.data?.message) setListErr(r.data.message);
    } catch (err) {
      setActionError(err.response?.data?.error || err.message || 'Reschedule failed.');
    } finally {
      setActionBusy(null);
    }
  };

  const fullAddress = form.property_address
    ? `${form.property_address}, ${form.property_city} ${form.property_state} ${form.property_zip}`.trim()
    : '';

  const remaining = credits?.credits_remaining ?? null;
  const needsPayment = credits && remaining <= 0;

  // ── Form view ──
  const renderForm = () => {
    if (finalizing) {
      return (
        <div className="px-6 py-16 text-center">
          <div className="inline-block w-8 h-8 border-[3px] border-gray-200 border-t-[#3a6ad9] rounded-full animate-spin mb-4" />
          <p className="text-sm text-gray-600">Finalizing your request…</p>
        </div>
      );
    }

    if (done) {
      return (
        <div className="px-6 py-10 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-4">
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h3 className="text-xl font-bold text-black mb-2">Request Submitted!</h3>
          <p className="text-gray-500 text-sm mb-1">A licensed showing agent will be notified.</p>
          <p className="text-gray-500 text-sm">You'll get an email the moment someone accepts — usually within a few hours.</p>
          <div className="flex gap-2 mt-6">
            <button onClick={() => { setDone(false); setView('list'); }}
              className="flex-1 h-11 rounded-xl border border-gray-200 text-sm font-semibold text-gray-700 hover:bg-gray-50">
              View My Showings
            </button>
            <button onClick={onClose} className="flex-1 h-11 rounded-xl text-white font-semibold text-sm" style={{ backgroundColor: '#3a6ad9' }}>
              Close
            </button>
          </div>
        </div>
      );
    }

    const labelCls = 'block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5';
    const inputCls = 'w-full h-11 px-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#3a6ad9]/30';

    return (
      <form onSubmit={handleSubmit} className="overflow-y-auto flex-1 px-6 py-5 space-y-4">
        {/* Credit / payment banner */}
        {credits && (
          needsPayment ? (
            <div className="rounded-xl px-4 py-3 text-sm" style={{ background: '#fffbef', border: '1px solid #ffe4a0', color: '#7a5200' }}>
              <div className="font-bold mb-0.5">You've used all 3 complimentary showings.</div>
              <div className="text-xs">Each additional showing is <strong>$60</strong>. You'll be prompted to pay after you submit the form.</div>
            </div>
          ) : (
            <div className="rounded-xl px-4 py-2.5 text-xs flex items-center justify-between" style={{ background: '#f0f4ff', border: '1px solid #c5d3f5', color: '#1e40af' }}>
              <span>
                <strong>{remaining}</strong> of {credits.credits_total} complimentary showing{remaining === 1 ? '' : 's'} remaining.
              </span>
              <span className="text-[11px] text-[#3a6ad9] font-semibold">Free</span>
            </div>
          )
        )}

        {submitErr && <div className="text-sm text-red-600 bg-red-50 rounded-xl px-4 py-3">{submitErr}</div>}

        <div>
          <label className={labelCls}>Street Address</label>
          <AddressAutocomplete
            name="showing_property_street"
            value={form.property_address}
            placeholder="Start typing an address..."
            onChange={(e) => setForm(p => ({ ...p, property_address: e.target.value }))}
            onSelect={({ street, city, state, zip }) =>
              setForm(p => ({
                ...p,
                property_address: street || p.property_address,
                property_city:    city   || p.property_city,
                property_state:   (state && US_STATES.includes(state)) ? state : p.property_state,
                property_zip:     zip    || p.property_zip,
              }))
            }
            className={inputCls}
            autoComplete="street-address"
          />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="sm:col-span-2">
            <label className={labelCls}>City</label>
            <input required type="text" value={form.property_city} onChange={setField('property_city')} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>State</label>
            <select required value={form.property_state} onChange={setField('property_state')} className={inputCls}>
              {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className={labelCls}>ZIP</label>
            <input required type="text" inputMode="numeric" pattern="[0-9]{5}" value={form.property_zip} onChange={setField('property_zip')} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Date</label>
            <input required type="date" min={earliestValidDate()} max={maxDate()} value={form.requested_date}
              onChange={(e) => {
                // Clear the time when the date changes — prevents the user
                // submitting a previously-selected time that's no longer in
                // the date's filtered slot list (e.g. was a today-only slot).
                setForm(p => ({ ...p, requested_date: e.target.value, requested_time: '' }));
                if (submitErr) setSubmitErr('');
              }}
              className={inputCls} />
          </div>
        </div>
        <div>
          <label className={labelCls}>Time</label>
          <select required value={form.requested_time} onChange={setField('requested_time')} className={inputCls}>
            <option value="">Select a time…</option>
            {TIME_SLOTS.filter(s => !isTimeSlotDisabled(form.requested_date, s.value)).map(s => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
          <p className="text-[11px] text-gray-400 mt-1">⏰ Showings require at least {MIN_NOTICE_HOURS} hours advance notice. 8:00 AM – 8:45 PM, 15-minute increments.</p>
        </div>

        <div>
          <label className={labelCls}>Phone for Calls & Texts</label>
          <input required type="tel" inputMode="tel" autoComplete="tel"
            value={form.contact_phone}
            onChange={(e) => setForm(p => ({ ...p, contact_phone: formatPhone(e.target.value) }))}
            placeholder="(555) 123-4567"
            className={inputCls} />
          <p className="text-[11px] text-gray-400 mt-1">The showing agent will use this number to coordinate access.</p>
        </div>

        <button type="submit" disabled={submitting}
          className="btn-primary btn-full text-white disabled:opacity-60"
          style={{ backgroundColor: '#3a6ad9' }}>
          {submitting ? 'Submitting…' : (
            <>
              <span>{needsPayment ? 'Continue to Payment' : 'Request Showing'}</span>
              <span className="btn-arrow"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M9 5l7 7-7 7" /></svg></span>
            </>
          )}
        </button>
        <p className="text-xs text-gray-400 text-center -mt-1">The showing agent will coordinate an exact time within your selected window.</p>
      </form>
    );
  };

  // ── List view ──
  const renderList = () => (
    <div className="overflow-y-auto flex-1 px-6 py-5 space-y-3">
      {loadingList && <div className="text-sm text-gray-400 italic">Loading…</div>}
      {listErr && !loadingList && <div className="text-sm text-red-600 bg-red-50 rounded-xl px-4 py-3">Error: {listErr}</div>}
      {!loadingList && !listErr && requests.length === 0 && (
        <div className="bg-gray-50 border border-gray-100 rounded-xl p-6 text-sm text-gray-500 text-center">
          No showing requests yet. Schedule one from a property card.
        </div>
      )}
      {requests.map(r => {
        const addr = [r.address, r.property_city, r.property_state, r.property_zip].filter(Boolean).join(', ');
        const cancelable = r.status !== 'completed' && r.status !== 'canceled';
        return (
          <div key={r.id} className="bg-white rounded-xl border border-gray-100 p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <StatusBadge status={r.status} />
                  {r.showami_request_id && <span className="text-[11px] text-gray-400">#{r.showami_request_id}</span>}
                  {r.payment_status === 'paid' && <span className="text-[10px] font-bold text-green-700 bg-green-50 border border-green-200 rounded px-1.5 py-0.5">PAID</span>}
                </div>
                <div className="text-sm font-semibold text-gray-900 break-words">{addr || r.address}</div>
                <div className="text-xs text-gray-500 mt-0.5">{r.preferred_date} · {r.preferred_time}</div>
                {r.borrower_notes && <div className="text-xs text-gray-500 mt-2 italic">"{r.borrower_notes}"</div>}
                {(r.showing_agent_name || r.showing_agent_phone || r.showing_agent_email) && (
                  <div className="mt-3 rounded-lg p-3" style={{ background: '#f0fdf4', border: '1px solid #bbf7d0' }}>
                    <div className="text-[11px] font-bold uppercase tracking-wider mb-2" style={{ color: '#15803d' }}>
                      🧑‍💼 Your Showing Agent
                    </div>
                    {r.showing_agent_name && (
                      <div className="text-sm font-semibold text-gray-900 mb-1">{r.showing_agent_name}</div>
                    )}
                    <div className="flex flex-wrap gap-x-4 gap-y-1">
                      {r.showing_agent_phone && (
                        <a href={`tel:${String(r.showing_agent_phone).replace(/\D/g, '')}`}
                          className="text-sm font-medium" style={{ color: '#3a6ad9', textDecoration: 'none' }}>
                          📞 {r.showing_agent_phone}
                        </a>
                      )}
                      {r.showing_agent_email && (
                        <a href={`mailto:${r.showing_agent_email}`}
                          className="text-sm font-medium" style={{ color: '#3a6ad9', textDecoration: 'none' }}>
                          ✉️ {r.showing_agent_email}
                        </a>
                      )}
                    </div>
                  </div>
                )}
                {r.agent_feedback && (
                  <div className="mt-3 pt-3 border-t border-gray-100 text-xs text-gray-600">
                    <div className="font-bold text-gray-400 uppercase tracking-wider text-[10px] mb-1">Agent Feedback</div>
                    {r.agent_feedback}
                  </div>
                )}
                {/* Cancellation reason — pulled from the Showami webhook
                    payload when the agent / Showami provides one. */}
                {r.cancelled_reason && (
                  <div className="mt-3 pt-3 border-t border-gray-100">
                    <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Cancellation Reason</div>
                    <div className="text-xs text-red-700 bg-red-50 border border-red-100 rounded px-2 py-1.5">{r.cancelled_reason}</div>
                  </div>
                )}
                {/* Message thread — read-only sync from Showami. The
                    "View Messages" toggle lazy-loads the thread + marks
                    it read on the server. Replies must go through
                    Showami directly. */}
                {(r.status === 'Accepted' || r.status === 'Confirmed' || r.status === 'Completed' || r.showing_agent_name) && (
                  <div className="mt-3 pt-3 border-t border-gray-100">
                    <button
                      type="button"
                      onClick={() => {
                        const opening = threadOpenId !== r.id;
                        setThreadOpenId(opening ? r.id : null);
                        if (opening && threadById[r.id] === undefined) {
                          axios.get(`${API_URL}/api/showami/requests/${r.id}/messages`, { headers })
                            .then((resp) => setThreadById((prev) => ({ ...prev, [r.id]: resp.data.messages || [] })))
                            .catch(() => setThreadById((prev) => ({ ...prev, [r.id]: [] })));
                        }
                      }}
                      className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1 inline-flex items-center gap-2 hover:text-gray-600"
                    >
                      Messages with Showing Agent
                      <span className="text-gray-300">{threadOpenId === r.id ? '▾' : '▸'}</span>
                    </button>
                    {threadOpenId === r.id && (
                      <div className="mt-2 flex flex-col gap-2">
                        {threadById[r.id] === undefined && (
                          <div className="text-xs text-gray-400 italic">Loading messages…</div>
                        )}
                        {threadById[r.id] && threadById[r.id].length === 0 && (
                          <div className="text-xs text-gray-400 bg-gray-50 border border-dashed border-gray-200 rounded px-3 py-2">
                            No messages from your showing agent yet.
                          </div>
                        )}
                        {threadById[r.id] && threadById[r.id].length > 0 && threadById[r.id].map((m) => {
                          const isAgent = m.sender_type === 'agent';
                          return (
                            <div key={m.id} className={isAgent
                              ? 'bg-blue-50 border border-blue-100 border-l-[3px] border-l-[#3a6ad9] rounded-md px-3 py-2'
                              : 'bg-gray-50 border border-gray-100 border-l-[3px] border-l-gray-400 rounded-md px-3 py-2'}
                            >
                              <div className="flex items-center gap-2 mb-1">
                                <span className="text-xs font-semibold text-gray-900">{m.sender_name || (isAgent ? 'Showing Agent' : 'Doorly')}</span>
                                <span className="ml-auto text-[10px] text-gray-400">
                                  {m.received_at ? new Date(m.received_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : ''}
                                </span>
                              </div>
                              <div className="text-xs text-gray-800 whitespace-pre-wrap leading-relaxed">{m.message_text}</div>
                            </div>
                          );
                        })}
                        <div className="text-[10px] text-gray-400 italic mt-1">
                          Your Doorly team coordinates communication with your showing agent. Email <a href="mailto:support@godoorly.com" style={{ color: '#3a6ad9', textDecoration: 'none' }}>support@godoorly.com</a> if you need to send anything to them.
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="flex flex-col gap-2 flex-shrink-0">
                {r.showami_request_id && (
                  <button onClick={() => refreshStatus(r.id)} disabled={refreshingId === r.id}
                    className="text-xs px-3 py-1.5 rounded-lg border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50">
                    {refreshingId === r.id ? '…' : 'Refresh'}
                  </button>
                )}
                {cancelable && (
                  <button onClick={() => handleRescheduleClicked(r)}
                    disabled={actionBusy === `reschedule-${r.id}`}
                    className="text-xs px-3 py-1.5 rounded-lg border border-[#3a6ad9] bg-white text-[#3a6ad9] hover:bg-blue-50 disabled:opacity-50 font-semibold">
                    Reschedule
                  </button>
                )}
                {cancelable && (
                  <button onClick={() => handleCancelClicked(r)}
                    disabled={actionBusy === `cancel-${r.id}`}
                    className="text-xs px-3 py-1.5 rounded-lg border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50">
                    Cancel
                  </button>
                )}
              </div>
            </div>

            {/* Inline reschedule form — shown when this row is the
                active reschedule target. 24-hour gating mirrors the
                request form via shared util. */}
            {rescheduleForId === r.id && (
              <div className="mt-4 pt-4 border-t border-gray-100">
                <div className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">
                  Reschedule
                </div>
                <div className="grid grid-cols-2 gap-3 mb-3">
                  <div>
                    <label className="text-xs font-semibold text-gray-600 block mb-1">New Date</label>
                    <input type="date" min={earliestValidDate()} max={maxDate()}
                      value={rescheduleForm.requested_date}
                      onChange={(e) => setRescheduleForm((p) => ({ ...p, requested_date: e.target.value, requested_time: '' }))}
                      className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:border-[#3a6ad9] focus:ring-1 focus:ring-[#3a6ad9] outline-none" />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-600 block mb-1">New Time</label>
                    <select value={rescheduleForm.requested_time}
                      onChange={(e) => setRescheduleForm((p) => ({ ...p, requested_time: e.target.value }))}
                      className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:border-[#3a6ad9] focus:ring-1 focus:ring-[#3a6ad9] outline-none">
                      <option value="">Select a time…</option>
                      {TIME_SLOTS.filter((s) => !isTimeSlotDisabled(rescheduleForm.requested_date, s.value)).map((s) => (
                        <option key={s.value} value={s.value}>{s.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <p className="text-[11px] text-gray-400 mb-3">
                  ⏰ At least {MIN_NOTICE_HOURS} hours from now. 8:00 AM – 8:45 PM, 15-minute increments.
                </p>
                {actionError && (
                  <div className="text-xs text-red-600 bg-red-50 border border-red-100 rounded-md px-3 py-2 mb-3">
                    {actionError}
                  </div>
                )}
                <div className="flex gap-2">
                  <button onClick={() => submitReschedule(r.id)}
                    disabled={actionBusy === `reschedule-${r.id}` || !rescheduleForm.requested_date || !rescheduleForm.requested_time}
                    className="flex-1 text-sm px-4 py-2 rounded-lg bg-[#3a6ad9] text-white font-semibold disabled:bg-gray-300 disabled:cursor-not-allowed">
                    {actionBusy === `reschedule-${r.id}` ? 'Confirming…' : 'Confirm Reschedule'}
                  </button>
                  <button onClick={() => { setRescheduleForId(null); setActionError(null); }}
                    disabled={actionBusy === `reschedule-${r.id}`}
                    className="text-sm px-4 py-2 rounded-lg border border-gray-200 bg-white text-gray-700 font-semibold disabled:opacity-50">
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );

  return (
    <>
      <div className="safe-modal-overlay fixed inset-0 z-50 flex items-center justify-center px-4 py-6" style={{ backgroundColor: 'rgba(0,0,0,0.55)' }}>
        <div className="safe-modal-box bg-white rounded-2xl shadow-2xl w-full max-w-lg flex flex-col" style={{ '--sm-max': '90vh' }}>
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
            <div className="min-w-0">
              <h2 className="text-lg font-bold text-black">
                {view === 'form' ? 'Request a Showing' : 'My Showings'}
              </h2>
              {view === 'form' && fullAddress && (
                <p className="text-xs text-gray-400 mt-0.5 truncate">{fullAddress}</p>
              )}
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-700 p-1 flex-shrink-0 ml-2" aria-label="Close">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="flex border-b border-gray-100 flex-shrink-0">
            {[
              { id: 'form', label: 'New Request' },
              { id: 'list', label: 'My Showings' },
            ].map(tab => (
              <button key={tab.id}
                onClick={() => { setView(tab.id); setDone(false); }}
                className={`flex-1 py-2.5 text-sm font-semibold transition-colors ${
                  view === tab.id
                    ? 'text-[#3a6ad9] border-b-2 border-[#3a6ad9] bg-blue-50/30'
                    : 'text-gray-500 border-b-2 border-transparent hover:text-gray-700'
                }`}>
                {tab.label}
              </button>
            ))}
          </div>

          {view === 'form' ? renderForm() : renderList()}
        </div>
      </div>

      {showPayment && (
        <PaymentModal
          isOpen={showPayment}
          onClose={handlePaymentCancel}
          onSuccess={handlePaymentSuccess}
          type="showing_fee"
        />
      )}

      {/* Cancellation-policy modal — gates the final submit. Borrower
          has already passed all form validation by the time this
          renders; the only choice now is acknowledge or back out. */}
      {showCancellationPolicy && pendingForm && (
        <div className="safe-modal-overlay" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: 16 }}>
          <div style={{ background: 'white', borderRadius: 12, padding: 28, maxWidth: 420, width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 12 }}>📋 Before You Confirm</div>
            <div style={{ fontSize: 14, color: '#374151', lineHeight: 1.7, marginBottom: 20 }}>
              Your showing request for <strong>{pendingForm.property_address}</strong> on{' '}
              <strong>
                {pendingForm.requested_date} at {(() => {
                  const t = TIME_SLOTS.find((s) => s.value === pendingForm.requested_time);
                  return t ? t.label : pendingForm.requested_time;
                })()}
              </strong>{' '}
              is almost confirmed.
              <br /><br />
              <strong>Important:</strong> If you need to cancel or reschedule, please do so at least{' '}
              <strong>4 hours before your showing time</strong>. Cancellations or reschedules within 4 hours of the showing will result in the loss of your showing credit.
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => setShowCancellationPolicy(false)} disabled={submitting}
                style={{ flex: 1, padding: 11, border: '1px solid #e5e7eb', borderRadius: 8, background: 'white', color: '#374151', fontWeight: 600, cursor: 'pointer', fontSize: 14 }}>
                Go Back
              </button>
              <button onClick={handleConfirmSubmit} disabled={submitting}
                style={{ flex: 1, padding: 11, border: 'none', borderRadius: 8, background: '#3a6ad9', color: 'white', fontWeight: 600, cursor: submitting ? 'wait' : 'pointer', fontSize: 14 }}>
                {submitting ? 'Submitting…' : 'Confirm Showing'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel / Reschedule confirmation modal. Renders when
          confirmAction is set by handleCancelClicked / the within-4h
          branch of handleRescheduleClicked. Copy + button labels
          adapt to the kind + within4h pair so the borrower sees
          either "credit refunded" or "credit forfeited" framing. */}
      {confirmAction && (
        <div className="safe-modal-overlay" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: 16 }}>
          <div style={{ background: 'white', borderRadius: 12, padding: 28, maxWidth: 440, width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            {confirmAction.kind === 'cancel' && confirmAction.within4h && (<>
              <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 12, color: '#b45309' }}>
                ⚠ Cancelling within 4 hours
              </div>
              <div style={{ fontSize: 14, color: '#374151', lineHeight: 1.7, marginBottom: 20 }}>
                Cancelling this showing will result in the <strong>loss of your showing credit</strong>. This cannot be undone.
              </div>
            </>)}
            {confirmAction.kind === 'cancel' && !confirmAction.within4h && (<>
              <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 12 }}>Cancel this showing?</div>
              <div style={{ fontSize: 14, color: '#374151', lineHeight: 1.7, marginBottom: 20 }}>
                Your showing at <strong>{confirmAction.showing.address}</strong> on{' '}
                <strong>{confirmAction.showing.preferred_date} at {confirmAction.showing.preferred_time}</strong> will be cancelled.
                Your showing credit will be returned.
              </div>
            </>)}
            {confirmAction.kind === 'reschedule' && confirmAction.within4h && (<>
              <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 12, color: '#b45309' }}>
                ⚠ Rescheduling within 4 hours
              </div>
              <div style={{ fontSize: 14, color: '#374151', lineHeight: 1.7, marginBottom: 20 }}>
                Rescheduling this showing within 4 hours of the scheduled time will result in the <strong>loss of your showing credit</strong>.
              </div>
            </>)}
            {actionError && (
              <div style={{ fontSize: 12, color: '#dc2626', marginBottom: 12 }}>{actionError}</div>
            )}
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => { setConfirmAction(null); setActionError(null); }}
                disabled={!!actionBusy}
                style={{ flex: 1, padding: 11, border: '1px solid #e5e7eb', borderRadius: 8, background: 'white', color: '#374151', fontWeight: 600, cursor: 'pointer', fontSize: 14 }}>
                Keep My Showing
              </button>
              {confirmAction.kind === 'cancel' ? (
                <button onClick={confirmCancel}
                  disabled={!!actionBusy}
                  style={{ flex: 1, padding: 11, border: 'none', borderRadius: 8, background: confirmAction.within4h ? '#dc2626' : '#3a6ad9', color: 'white', fontWeight: 600, cursor: actionBusy ? 'wait' : 'pointer', fontSize: 14 }}>
                  {actionBusy ? 'Cancelling…' : (confirmAction.within4h ? 'Cancel Anyway — Lose Credit' : 'Yes, Cancel')}
                </button>
              ) : (
                <button onClick={confirmRescheduleProceed}
                  disabled={!!actionBusy}
                  style={{ flex: 1, padding: 11, border: 'none', borderRadius: 8, background: '#dc2626', color: 'white', fontWeight: 600, cursor: 'pointer', fontSize: 14 }}>
                  Reschedule — Lose Credit
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
